import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientProfile, setClientProfile] = useState(null);
  const initCalled = useRef(false);

  const initAuth = useCallback(async () => {
    if (initCalled.current) return;
    initCalled.current = true;
    try {
      const { data } = await api.get('/auth/me');
      if (data?.user) {
        setUser(data.user);
      } else {
        setUser(data);
      }
    } catch (err) {
      // No localStorage token; cookie-based auth will naturally fail if not logged in
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const registerClient = useCallback(async (fullName, mobile, countryCode = '+91') => {
    const digits = (mobile || '').replace(/\D/g, '');
    const fullMobile = digits ? (String(mobile).trim().startsWith('+') ? `+${digits}` : `${countryCode}${digits}`) : '';
    const profile = {
      full_name: fullName.trim(),
      mobile: fullMobile,
      country_code: countryCode,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem('client_profile', JSON.stringify(profile));
    setClientProfile(profile);
    return profile;
  }, []);

  const startGuestChat = useCallback(async (fullName = '') => {
    const profile = {
      full_name: fullName.trim() || 'Guest',
      mobile: '',
      country_code: '',
      guest: true,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem('client_profile', JSON.stringify(profile));
    setClientProfile(profile);
    return profile;
  }, []);

  const sendOTP = useCallback(async (mobile, countryCode = '+91') => {
    const { data } = await api.post('/auth/otp/send', { mobile, country_code: countryCode });
    return data;
  }, []);

  const verifyOTP = useCallback(async (mobile, otp, countryCode = '+91') => {
    const { data } = await api.post('/auth/otp/verify', {
      mobile,
      country_code: countryCode,
      code: otp,
    });
    if (!data?.verified) {
      throw new Error('Invalid or expired verification code');
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Clear local state even if the API is temporarily unavailable.
    } finally {
      localStorage.removeItem('client_profile');
      setUser(null);
      setClientProfile(null);
      window.location.href = '/login';
    }
  }, []);

  const googleAuth = useCallback(async (sessionId) => {
    const { data } = await api.post('/auth/google', { session_id: sessionId });
    if (data.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const isStaff = user?.role && ['admin', 'lawyer', 'agent'].includes(user.role.toLowerCase());
  const isClient = !!clientProfile && !isStaff;

  const value = {
    user,
    clientProfile,
    loading,
    login,
    logout,
    googleAuth,
    registerClient,
    sendOTP,
    verifyOTP,
    startGuestChat,
    isAuthenticated: !!user || !!clientProfile,
    isStaff,
    isClient,
    isAdmin: user?.role?.toLowerCase() === 'admin',
    isLawyer: user?.role?.toLowerCase() === 'lawyer',
    isAgent: user?.role?.toLowerCase() === 'agent',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
